// Use createSelector from reselect to cache computations.
// see: https://github.com/reduxjs/reselect for details.
import moment from "moment";
import { createSelector } from "reselect";
import { compositeKeyString } from "../util/panel";

import { defaultFrame, defaultFrameList, defaultImage } from "./default";
export const getTime = state => moment(state.app.time);
export const getPanels = state => state.panels;
export const getActiveWorkspaceId = state => state.app.activeWorkspace;
export const getWorkspaces = state => state.workspaces;
export const getNextId = state => state.app.nextId;
export const getImages = state => state.images;

// Returns the "next" workspace that appears after the target workspace. If the target workspace is
// the last one in the list, the result is the workspace that appears immediately before the target.
export const getNextWorkspace = (state, workspaceId) => {
  const nextWorkspace = state.workspaces.filter(workspace => {
    return workspace.id !== workspaceId;
  })[0];
  if (nextWorkspace) {
    return nextWorkspace.id;
  } else {
    return null;
  }
};

/** Find a framelist by id, in the state
 * @param {Object} state - the application state
 * @param {number} framelistID - id of desired framelist
 * @returns {?Object} The found framelist, or null.
 */
export function getFrameList(state, framelistID) {
  for (let i = 0; i < state.images.items.length; i++) {
    const item = state.images.items[i];
    if (!item.frameLists) {
      continue;
    }
    const frameList = item.frameLists.find(x => x.id === framelistID);
    if (frameList) {
      return frameList;
    }
  }
  return null;
}

export function getFrameListsForPanel(state, panelId) {
  const productData = getProductDataForActiveWorkspacePanels(state);
  return productData[panelId].frameLists;
}

// Determines which frame the given panel is looking at, and returns the array index of the frame list's
// frames array. This is primarily used for "next" and "previous" cycling.
export const getFrameIndexForPanel = (state, panelId) => {
  const panel = state.panels.find(panel => panel.id === panelId);
  let frameIndex = 0;
  // Find the imageItem that matches the composite key of the target panel
  const imageItem = state.images.items.find(imageItem => {
    // This rather complex line is creating an array of the compositeKey keys. It is then iterating
    // over those keys, and comparing the value of each key in the imageItem object to the value of
    // the same key in the panel's compositeKey object.
    // This will return true only if every key is a complete match.
    return Object.keys(panel.compositeKey).every(
      key => imageItem[key] === panel.compositeKey[key]
    );
  });

  // Now find the framelist that is selected in the panel
  const activeImage = calculateAndReturnSelectionForPanel(
    imageItem,
    panel.userSelected || {}
  )(state);
  if (activeImage.frameList) {
    frameIndex = activeImage.frameList.frames.findIndex(
      frame => frame.id === activeImage.frame.id
    );
  }

  // Though it is not typical to return an object in a selector, this function is mainly used by the
  // next and previous frame actions, and thus require both of these values to compute their next state.
  // Other components that may use this in the future can simply ignore the irrelevant key.
  return { frameList: activeImage.frameList, frameIndex };
};

// Returns the set of active framelist, frame, and most current image,
// or sensible defaults for all of those if no data exists.
// Returns:
// {
//  frameList: {...},     // The frameList that should be selected. Uses userSelected if possible and valid, otherwise the "most current"
//  frame: {...},         // The frame that should be selected. Uses userSelected if possible and valid, otherwise the "most current"
//  image: {...},         // The image that should be selected. Uses userSelected if possible and valid, otherwise the "most current"
//  panelIsCurrent: true, // Whether or not the panel is selecting all of the current data (frame and image)
// }
export const calculateAndReturnSelectionForPanel = (
  productData,
  userSelected
) => {
  return createSelector([getTime], currentTime => {
    var calculatedSelectionData = {
      frameList: null,
      panelIsCurrent: false
    };
    if (productData) {
      var frameLists = productData.frameLists;

      // If we already have a frameList selected; note that "userSelected.frameList" is an ID, not an object
      if (userSelected.frameList) {
        // Try to find it
        calculatedSelectionData.frameList = frameLists.find(
          frameList => frameList.id === userSelected.frameList
        );
      }

      // Failing that, fallback to default frameList given the currentTime and list of frameLists
      if (!calculatedSelectionData.frameList) {
        calculatedSelectionData.frameList = defaultFrameList(
          currentTime,
          frameLists
        );
      }

      // If the user has a frame selected for the frameList, use it
      if (userSelected.frame[calculatedSelectionData.frameList.id]) {
        calculatedSelectionData.frame =
          calculatedSelectionData.frameList.frames.find(
            frame =>
              frame.id ===
              userSelected.frame[calculatedSelectionData.frameList.id]
          );
      }

      const latestFrame = defaultFrame(
        currentTime,
        calculatedSelectionData.frameList.frames
      );
      if (!calculatedSelectionData.frame) {
        calculatedSelectionData.frame = latestFrame;
      }

      if (userSelected.image[calculatedSelectionData.frame.id]) {
        calculatedSelectionData.image =
          calculatedSelectionData.frame.images.find(
            image =>
              image.id === userSelected.image[calculatedSelectionData.frame.id]
          );
      }

      const latestImage = defaultImage(calculatedSelectionData.frame.images);
      if (!calculatedSelectionData.image) {
        calculatedSelectionData.image = latestImage;
      }

      // We are considered to be looking at "old" data if the selected frame is before the
      // current time, and/or selected image in that frame has been superseded by another
      // more recent one.
      if (
        calculatedSelectionData.image.id === latestImage.id &&
        calculatedSelectionData.frame.id === latestFrame.id
      ) {
        calculatedSelectionData.panelIsCurrent = true;
      }
    }
    return calculatedSelectionData;
  });
};

export const getActiveWorkspace = createSelector(
  [getWorkspaces, getActiveWorkspaceId],
  (workspaces, activeWorkspace) => {
    const current = workspaces.filter(w => w.id === activeWorkspace);
    return current.pop();
  }
);

export const getWorkspaceNames = createSelector([getWorkspaces], workspaces => {
  const workspaceNames = workspaces.map(workspace => workspace.name);
  return workspaceNames;
});

// This function returns a list of dictionaries containing the id and composite key of each panel
// for the active workspace
export const getPanelsForActiveWorkspace = createSelector(
  [getPanels, getActiveWorkspaceId],
  (panels, activeWorkspace) => {
    return panels
      .filter(p => p.workspace === activeWorkspace)
      .map(p => {
        return { id: p.id, compositeKey: p.compositeKey };
      });
  }
);

export const getNextWorkspaceId = createSelector(
  [getNextWorkspace],
  nextWorkspace => {
    return nextWorkspace;
  }
);

export const getProductDataForActiveWorkspacePanels = createSelector(
  [getImages, getPanelsForActiveWorkspace],
  (images, activePanels) => {
    const panelMap = {};
    if (images.items) {
      for (const panel of activePanels) {
        panelMap[panel.id] = images.items.filter(
          i => compositeKeyString(i) === compositeKeyString(panel.compositeKey)
        )[0];
      }
    }
    return panelMap;
  }
);

export const getImagesForPanels = createSelector([getPanels], panels => {
  const referencedImages = new Set();
  for (const panel of panels) {
    referencedImages.add(panel.compositeKey);
  }
  return referencedImages;
});

// Returns panel properties for a given panelId; not the _data_ for the panel, but things like size,
// position, and playback speed
export const getPanelProperties = panelId => {
  return createSelector([getPanels], panels => {
    return panels.filter(p => p.id === panelId)[0];
  });
};

export const getColourizedImageUrl = (panelId, imageUrl) => {
  return createSelector([getImages], images => {
    var panelImages = images.colourizedImageUrls[panelId];
    if (panelImages) {
      return panelImages[imageUrl];
    } else {
      return null;
    }
  });
};
